import React from "react";
import { Link, useNavigate } from "react-router-dom";
import styled from "styled-components";
import Select from "@paljs/ui/Select";
import { LayoutHeader } from "@paljs/ui/Layout";
import { EvaIcon } from "@paljs/ui/Icon";
import { breakpointDown } from "@paljs/ui/breakpoints";
import { Actions } from "@paljs/ui/Actions";
import jwt_decode from "jwt-decode";
import SettingsContext from "../Settings";
import { useAuth } from "../Contexts/Auth";
import { Button } from "@paljs/ui";

const HeaderStyle = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  ${breakpointDown("sm")`
    gap: 0.5rem;
  `}
`;

const Label = styled.span`
  display: flex;
  align-items: center;
`;

const SelectStyled = styled(Select)`
  min-width: 150px;
`;

const Divider = styled.div`
  width: 1px;
  height: 28px;
  background-color: ${({ theme }) => theme.backgroundBasicColor4};
`;

const UserArea = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.2rem;
`;

const UserName = styled.span`
  font-size: 0.8rem;
  font-weight: 600;
  color: ${({ theme }) => theme.textBasicColor};
  white-space: nowrap;
  line-height: 1.2;
`;

const CompanyName = styled.span`
  font-size: 0.8rem;
  font-weight: 300;
  color: ${({ theme }) => theme.textHintColor};
  white-space: nowrap;
  line-height: 1;
`;

const SessionBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 1px 6px;
  border-radius: 3px;
  font-size: 0.6rem;
  font-weight: 500;
  white-space: nowrap;
  line-height: 1.3;
  background-color: ${({ $urgent }) =>
    $urgent ? "rgba(255, 61, 113, 0.12)" : "rgba(0, 149, 255, 0.10)"};
  color: ${({ $urgent, theme }) =>
    $urgent ? theme.colorDanger500 : theme.colorInfo500};
`;

const SessionTime = styled.span`
  font-weight: 700;
`;

function getDecodedToken() {
  try {
    const token = localStorage.getItem("token");
    if (!token) return null;
    return jwt_decode(token);
  } catch {
    return null;
  }
}

function getTimeRemaining() {
  const decoded = getDecodedToken();
  if (!decoded?.exp) return null;
  const diff = decoded.exp * 1000 - Date.now();
  return diff > 0 ? diff : 0;
}

function formatTime(ms) {
  if (ms == null || ms <= 0) return "00:00";
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (n) => String(n).padStart(2, "0");
  if (hours > 0) return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  return `${pad(minutes)}:${pad(seconds)}`;
}

const Header = () => {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const { settings, saveSettings } = React.useContext(SettingsContext);

  const [remaining, setRemaining] = React.useState(getTimeRemaining);

  const decodedToken = getDecodedToken();
  const userName = decodedToken?.name;
  const companyName = decodedToken?.company;

  React.useEffect(() => {
    const interval = setInterval(() => {
      const time = getTimeRemaining();
      setRemaining(time);
      if (time != null && time <= 0) {
        clearInterval(interval);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    signOut();
    navigate("/login");
  };

  const isUrgent = remaining != null && remaining < 5 * 60 * 1000;

  const themeOptions = [
    {
      value: "default",
      label: (
        <Label>
          <EvaIcon name="droplet" options={{ fill: "#a6c1ff" }} />
          Padrão
        </Label>
      ),
    },
    {
      value: "dark",
      label: (
        <Label>
          <EvaIcon name="droplet" options={{ fill: "#192038" }} />
          Escuro
        </Label>
      ),
    },
    {
      value: "cosmic",
      label: (
        <Label>
          <EvaIcon name="droplet" options={{ fill: "#5a37b8" }} />
          Cósmico
        </Label>
      ),
    },
    {
      value: "corporate",
      label: (
        <Label>
          <EvaIcon name="droplet" options={{ fill: "#3366ff" }} />
          Claro
        </Label>
      ),
      selected: true,
    },
  ];

  return (
    <LayoutHeader fixed>
      <HeaderStyle>
        <Actions
          size="Medium"
          actions={[
            {
              content: (
                <a tag={Link} href="/">
                  <img
                    src="https://iotlog-images.s3.amazonaws.com/companies/99ea60e8-29d3-4bfa-b72e-f913ecd34fa0/1790688499498-ocp_cbo.png"
                    height={35}
                    style={{ height: 35 }}
                    alt="logo"
                  />
                </a>
              ),
            },
          ]}
        />

        <RightSection>
          <SelectStyled
            isSearchable={false}
            shape="SemiRound"
            placeholder="Themes"
            value={themeOptions.find((item) => item.value === settings.theme)}
            options={themeOptions}
            onChange={({ value }) => saveSettings({ theme: value })}
          />

          <Divider />

          <UserArea>
            <UserInfo>
              {userName && (
                <UserName>{userName}</UserName>
              )}
              {companyName && (
                <CompanyName>{companyName}</CompanyName>
              )}
              {remaining != null && (
                <SessionBadge $urgent={isUrgent}>
                  Sessão expira em: <SessionTime>{formatTime(remaining)}</SessionTime>
                </SessionBadge>
              )}
            </UserInfo>

            <Button
              size="Tiny"
              status="Basic"
              appearance="ghost"
              style={{ alignItems: "center", justifyContent: "center", display: "flex", border: "none" }}
              onClick={handleLogout}>
              Sair
              <EvaIcon
                name="log-out-outline"
                className="ml-1"
              />
            </Button>
          </UserArea>
        </RightSection>
      </HeaderStyle>
    </LayoutHeader>
  );
};
export default Header;
